import {
  Editor,
  Path,
  Point,
  Range,
  Transforms,
  Node
} from 'slate';

import * as basicEditorValueSerializer from 'basic-editor-value-serializer';

import { ensureEditorStores } from '../../stores/active-block';

import * as paragraphUtilities from './paragraph';
import * as headerStyles from './site-nav/styles';
import * as layoutUtilities from './layout';

const FRAGMENT_DATA_TYPE = 'application/x-vz-editor-fragment';
const FRAGMENT_HTML_ATTRIBUTE = 'data-vz-editor-fragment';
const STYLES_DATA_TYPE = 'application/x-vz-editor-styles';
const STYLES_HTML_ATTRIBUTE = 'data-vz-editor-styles';

const blockStyleProperties = [
  'alignment',
  'padding',
  'margin',
  'marginLeft',
  'marginRight',
  'radius',
  'border',
  'width',
  'height',
  'textColor',
  'backgroundColor',
  'boxShadow',
  'backgroundImage',
  'backgroundPosition',
  'backgroundSize'
];

let copiedBlockStyles = null;

function getClosestBlockPathAt(editor, path) {

  let entry = Editor.node(editor, path);
  let node = entry[0];
  let nodePath = entry[1];

  while (node) {

    if (basicEditorValueSerializer.draggableElements.includes(node.type) === true) {
      return nodePath;
    }

    if (nodePath.length === 0) {
      return null;
    }

    entry = Editor.parent(editor, nodePath);
    node = entry[0];
    nodePath = entry[1];

  }

  return null;

}

function getClosestBlockPath(editor) {

  return getClosestBlockPathAt(editor, editor.selection.anchor.path);

}

function getNodeMenuPath(editor) {

  const stores = ensureEditorStores(editor);
  const nodeMenuState = stores.nodeMenu.get();

  if (nodeMenuState.isOpen !== true) {
    return null;
  }

  return nodeMenuState.getPath();

}

function isWholeBlockSelected(editor, blockPath, selection) {

  const edges = Range.edges(selection);
  const blockStart = Editor.start(editor, blockPath);
  const blockEnd = Editor.end(editor, blockPath);

  if (Point.equals(edges[0], blockStart) === false) {
    return false;
  }

  return Point.equals(edges[1], blockEnd);

}

export function resolveCopyTargets(editor) {

  const nodeMenuPath = getNodeMenuPath(editor);

  if (nodeMenuPath !== null) {
    return { type: 'blocks', paths: [nodeMenuPath] };
  }

  const selection = editor.selection;

  if (selection === null) {
    return null;
  }

  const anchorBlockPath = getClosestBlockPathAt(editor, selection.anchor.path);
  const focusBlockPath = getClosestBlockPathAt(editor, selection.focus.path);

  if (anchorBlockPath === null || focusBlockPath === null) {
    return null;
  }

  if (Path.equals(anchorBlockPath, focusBlockPath) === true) {

    if (Range.isCollapsed(selection) === true || isWholeBlockSelected(editor, anchorBlockPath, selection) === true) {
      return { type: 'blocks', paths: [anchorBlockPath] };
    }

    return { type: 'inline', path: anchorBlockPath };

  }

  const commonAncestorPath = Path.common(anchorBlockPath, focusBlockPath);

  const blocksAreSiblings = anchorBlockPath.length === commonAncestorPath.length + 1 &&
    focusBlockPath.length === commonAncestorPath.length + 1;

  if (blocksAreSiblings === false && commonAncestorPath.length > 0) {

    const ancestorBlockPath = getClosestBlockPathAt(editor, commonAncestorPath);

    if (ancestorBlockPath !== null) {
      return { type: 'blocks', paths: [ancestorBlockPath] };
    }

  }

  const anchorChildIndex = anchorBlockPath[commonAncestorPath.length];
  const focusChildIndex = focusBlockPath[commonAncestorPath.length];
  const startIndex = Math.min(anchorChildIndex, focusChildIndex);
  const endIndex = Math.max(anchorChildIndex, focusChildIndex);

  const paths = [];

  for (let index = startIndex; index <= endIndex; index++) {
    paths.push(commonAncestorPath.concat([index]));
  }

  return { type: 'blocks', paths };

}

function encodeFragmentForHTML(fragmentJSON) {

  return window.btoa(encodeURIComponent(fragmentJSON));

}

function decodeFragmentFromHTML(encodedFragment) {

  return decodeURIComponent(window.atob(encodedFragment));

}

function wrapHTMLWithFragment(html, fragmentJSON) {

  return '<div ' + FRAGMENT_HTML_ATTRIBUTE + '="' + encodeFragmentForHTML(fragmentJSON) + '">' + html + '</div>';

}

function toMarkdown(nodes) {

  try {
    return basicEditorValueSerializer.markdown.toMarkdown(nodes);
  } catch (exception) {
    // eslint-disable-next-line no-console
    console.error(exception);
    return basicEditorValueSerializer.toText(nodes);
  }

}

function buildCopyResult(nodes, partial) {

  const fragmentJSON = JSON.stringify({ fragment: nodes, partial });

  return {
    fragment: fragmentJSON,
    html: wrapHTMLWithFragment(basicEditorValueSerializer.toNativeHTML(nodes), fragmentJSON),
    markdown: toMarkdown(nodes),
    text: basicEditorValueSerializer.toText(nodes)
  };

}

function buildInlineCopyResult(editor, blockPath) {

  const fragment = editor.getFragment();

  let checkedLevels = 0;
  let actualSelection = fragment;

  while (checkedLevels !== blockPath.length - 1) {
    actualSelection = actualSelection[0].children;
    checkedLevels++;
  }

  const partial = basicEditorValueSerializer.textElementsV2.includes(actualSelection[0].type);

  return buildCopyResult(actualSelection, partial);

}

export function determineCopiedContent(editor) {

  const targets = resolveCopyTargets(editor);

  if (targets === null) {
    return null;
  }

  if (targets.type === 'inline') {
    return buildInlineCopyResult(editor, targets.path);
  }

  const nodes = [];

  for (const path of targets.paths) {
    nodes.push(Node.get(editor, path));
  }

  rememberBlockStyles(nodes);

  return buildCopyResult(nodes, false);

}

export function copySelection(event, editor) {

  const result = determineCopiedContent(editor);

  if (result === null) {
    return false;
  }

  event.clipboardData.setData('text/plain', result.text);
  event.clipboardData.setData('text/html', result.html);
  event.clipboardData.setData('text/markdown', result.markdown);
  event.clipboardData.setData(FRAGMENT_DATA_TYPE, result.fragment);

  event.preventDefault();

  return true;

}

function copyThroughTemporarySelection(setClipboardData) {

  function handleCopyEvent(event) {
    setClipboardData(event.clipboardData);
    event.preventDefault();
    event.stopPropagation();
  }

  const holder = document.createElement('textarea');
  holder.value = ' ';
  holder.style.position = 'fixed';
  holder.style.top = '0';
  holder.style.left = '0';
  holder.style.opacity = '0';

  const previouslyFocusedElement = document.activeElement;

  const domSelection = window.getSelection();
  const savedRanges = [];

  for (let index = 0; index < domSelection.rangeCount; index++) {
    savedRanges.push(domSelection.getRangeAt(index));
  }

  document.body.appendChild(holder);

  holder.focus();
  holder.select();

  document.addEventListener('copy', handleCopyEvent, true);
  document.execCommand('copy');
  document.removeEventListener('copy', handleCopyEvent, true);

  document.body.removeChild(holder);

  previouslyFocusedElement.focus();

  domSelection.removeAllRanges();

  for (const range of savedRanges) {
    domSelection.addRange(range);
  }

}

function writeResultToClipboard(result) {

  copyThroughTemporarySelection(function setCopiedContent(clipboardData) {
    clipboardData.setData('text/plain', result.text);
    clipboardData.setData('text/html', result.html);
    clipboardData.setData('text/markdown', result.markdown);
    clipboardData.setData(FRAGMENT_DATA_TYPE, result.fragment);
  });

}

export function copyBlocks(editor, paths) {

  const nodes = [];

  for (const path of paths) {
    nodes.push(Node.get(editor, path));
  }

  rememberBlockStyles(nodes);

  writeResultToClipboard(buildCopyResult(nodes, false));

}

export function copySelectionToClipboard(editor) {

  const result = determineCopiedContent(editor);

  if (result === null) {
    return false;
  }

  writeResultToClipboard(result);

  return true;

}

function getHTMLAttributeValue(html, attribute) {

  if (typeof html !== 'string' || html.indexOf(attribute) === -1) {
    return null;
  }

  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
  const holder = parsedDocument.querySelector('[' + attribute + ']');

  if (holder === null) {
    return null;
  }

  return holder.getAttribute(attribute);

}

function writeStylesToClipboard(styles) {

  const stylesJSON = JSON.stringify(styles);
  const html = '<div ' + STYLES_HTML_ATTRIBUTE + '="' + encodeFragmentForHTML(stylesJSON) + '"></div>';

  copyThroughTemporarySelection(function setCopiedStyles(clipboardData) {
    clipboardData.setData('text/plain', '');
    clipboardData.setData('text/html', html);
    clipboardData.setData(STYLES_DATA_TYPE, stylesJSON);
  });

}

function extractStylesData(clipboardData) {

  const data = clipboardData.getData(STYLES_DATA_TYPE);

  if (data !== '') {
    return JSON.parse(data);
  }

  const encodedStyles = getHTMLAttributeValue(clipboardData.getData('text/html'), STYLES_HTML_ATTRIBUTE);

  if (encodedStyles === null) {
    return null;
  }

  return JSON.parse(decodeFragmentFromHTML(encodedStyles));

}

function extractFragmentFromHTML(html) {

  const encodedFragment = getHTMLAttributeValue(html, FRAGMENT_HTML_ATTRIBUTE);

  if (encodedFragment === null) {
    return null;
  }

  return decodeFragmentFromHTML(encodedFragment);

}

function extractFragmentData(clipboardData) {

  const data = clipboardData.getData(FRAGMENT_DATA_TYPE);

  if (data !== '') {
    return data;
  }

  return extractFragmentFromHTML(clipboardData.getData('text/html'));

}

export function pasteClipboardContent(event, editor) {

  const styles = extractStylesData(event.clipboardData);

  if (styles !== null) {
    return applyBlockStyles(editor, styles);
  }

  const data = extractFragmentData(event.clipboardData);

  if (data === null) {
    return false;
  }

  const parsedData = JSON.parse(data);

  if (parsedData.partial === true) {
    editor.insertFragment(parsedData.fragment);
    return true;
  }

  insertFragment(editor, parsedData.fragment);

  return true;

}

function cloneValue(value) {

  return JSON.parse(JSON.stringify(value));

}

function resolveStyleTargetPaths(editor, path) {

  if (typeof path !== 'undefined') {
    return [path];
  }

  const targets = resolveCopyTargets(editor);

  if (targets === null) {
    return null;
  }

  if (targets.type === 'inline') {
    return [targets.path];
  }

  return targets.paths;

}

function collectNodeStyles(node) {

  const styles = {};

  for (const property of blockStyleProperties) {

    if (typeof node[property] !== 'undefined') {
      styles[property] = node[property];
    }

  }

  if (node.type === basicEditorValueSerializer.allTypes.types.LAYOUT && typeof (node.properties || {}).layout !== 'undefined') {
    styles.layout = node.properties.layout;
  }

  const header = headerStyles.collect(node);
  if (header !== null) {
    styles.siteNav = header;
  }
  return styles;

}

function hasBlockStyles(node) {

  return Object.keys(collectNodeStyles(node)).length > 0;

}

function rememberBlockStyles(nodes) {

  if (nodes.length !== 1) {
    return;
  }

  copiedBlockStyles = cloneValue(collectNodeStyles(nodes[0]));

}

export function copyBlockStyles(editor, path) {

  const paths = resolveStyleTargetPaths(editor, path);

  if (paths === null || paths.length !== 1) {
    return false;
  }

  const node = Node.get(editor, paths[0]);

  copiedBlockStyles = cloneValue(collectNodeStyles(node));

  writeStylesToClipboard(copiedBlockStyles);

  return true;

}

function applyBlockStyles(editor, styles, path) {

  const paths = resolveStyleTargetPaths(editor, path);

  if (paths === null) {
    return false;
  }

  const missingProperties = [];

  for (const property of blockStyleProperties) {

    if (typeof styles[property] === 'undefined') {
      missingProperties.push(property);
    }

  }

  const nodeStyles = cloneValue(styles);

  delete nodeStyles.layout;
  delete nodeStyles.siteNav;

  for (const targetPath of paths) {

    Transforms.setNodes(editor, headerStyles.apply(Node.get(editor, targetPath), styles.siteNav, cloneValue(nodeStyles)), { at: targetPath });

    if (missingProperties.length > 0) {
      Transforms.unsetNodes(editor, missingProperties, { at: targetPath });
    }

    layoutUtilities.pasteLayoutSettings(editor, styles.layout, targetPath);

  }

  return true;

}

export function pasteBlockStyles(editor, path) {

  if (copiedBlockStyles === null) {
    return false;
  }

  return applyBlockStyles(editor, copiedBlockStyles, path);

}

function insertNodesReplacingEmptyBlock(editor, nodes, closestBlockPath) {

  if (layoutUtilities.isInRow(editor, closestBlockPath) === true) {
    layoutUtilities.pasteIntoRowItem(editor, closestBlockPath, nodes);
    return;
  }

  const closestBlock = Node.get(editor, closestBlockPath);
  const isBlockVoid = Editor.isVoid(editor, closestBlock);
  const isBlockEmpty = Node.string(closestBlock).length === 0;

  if (isBlockEmpty === false || isBlockVoid === true) {
    Transforms.insertNodes(editor, nodes);
    return;
  }

  Transforms.insertNodes(editor, nodes, { at: closestBlockPath });

  const parentPath = closestBlockPath.slice(0, -1);
  const blockIndex = closestBlockPath[closestBlockPath.length - 1];
  const emptyBlockPath = parentPath.concat([blockIndex + nodes.length]);
  const lastInsertedPath = parentPath.concat([blockIndex + nodes.length - 1]);

  Transforms.removeNodes(editor, { at: emptyBlockPath });
  Transforms.select(editor, Editor.end(editor, lastInsertedPath));

}

export function insertFragment(editor, fragment) {

  let closestBlockPath = getClosestBlockPath(editor);

  if (!closestBlockPath) {
    return;
  }

  try {

    Editor.withoutNormalizing(editor, () => {

      const closestBlock = Editor.node(editor, closestBlockPath)[0];

      const nodes = fragment.slice();

      if (fragment.length === 1) {

        const nodeType = fragment[0].type;

        if (basicEditorValueSerializer.allTypes.textBlocks.includes(nodeType) === true && hasBlockStyles(fragment[0]) === false) {
          Transforms.insertNodes(editor, fragment[0].children, { at: Path.next(editor.selection.focus.path) });
          Transforms.move(editor, { distance: Node.string(fragment[0]).length, unit: 'character' });
        } else if (basicEditorValueSerializer.allTypes.types.NUMBERED_LIST === nodeType || basicEditorValueSerializer.allTypes.types.BULLETED_LIST === nodeType) {
          if (nodes[0].children.length === 1) {
            Editor.insertFragment(editor, nodes);
          } else {
            insertNodesReplacingEmptyBlock(editor, nodes, closestBlockPath);
          }
        } else if (basicEditorValueSerializer.allTypes.types.TOGGLE_LIST_ITEM === nodeType) {
          Transforms.insertText(editor, Node.string(fragment[0]));
        } else {
          insertNodesReplacingEmptyBlock(editor, nodes, closestBlockPath);
        }

      } else if (layoutUtilities.isInRow(editor, closestBlockPath) === true) {
        layoutUtilities.pasteIntoRowItem(editor, closestBlockPath, nodes);
      } else {
        Transforms.insertNodes(editor, nodes, { at: Path.next(closestBlockPath) });
        Transforms.select(editor, {
          anchor: Editor.start(editor, Path.next(closestBlockPath)),
          focus: Editor.end(editor, [...closestBlockPath.slice(0, -1), closestBlockPath[closestBlockPath.length - 1] + nodes.length])
        });
        const [fileNode] = Editor.nodes(editor, {
          at: [...closestBlockPath.slice(0, -1), closestBlockPath[closestBlockPath.length - 1] + nodes.length],
          match: n => n.type === basicEditorValueSerializer.allTypes.types.HT_FILE,
          mode: 'lowest'
        });
        if (fileNode) {
          paragraphUtilities.insert(editor, Path.next(fileNode[1]));
        }
        Transforms.collapse(editor, { edge: 'end' });
        if (Node.string(closestBlock).length === 0 && Editor.isVoid(editor, closestBlock) === false) {
          Transforms.removeNodes(editor, { at: closestBlockPath });
        }
      }

    });

  } catch (exception) {

    // eslint-disable-next-line no-console
    console.error(exception);

  }

}
